/**
 * Output formatting utilities for CLI display.
 * Supports both human-readable (text, tables, colors) and JSON output.
 */

import { Chalk } from 'chalk';
import Table from 'cli-table3';
import terminalLink from 'terminal-link';
import { buildTaskLinkFromTicketWithBase, buildProjectLink } from '../../../../src/utils/task-link.js';

export interface FormatOptions {
  json?: boolean;
}

export type TableCell = unknown;
export type TableRow = TableCell[];

export interface CliTable {
  push(row: TableRow): void;
  toString(): string;
}

const ANSI_PATTERN =
  /[\u001B\u009B][[\]()#;?]*(?:(?:(?:[a-zA-Z\d]*(?:;[a-zA-Z\d]*)*)?\u0007)|(?:(?:\d{1,4}(?:;\d{0,4})*)?[\dA-PR-TZcf-nq-uy=><~]))/g;

const plainChalk = new Chalk({ level: 0 });
const colorChalk = new Chalk({ level: 1 });

export function isInteractiveOutput(): boolean {
  return process.stdout.isTTY === true;
}

export function shouldUseAnsi(): boolean {
  return isInteractiveOutput() && process.env.NO_COLOR === undefined;
}

function activeChalk(): typeof plainChalk {
  return shouldUseAnsi() ? colorChalk : plainChalk;
}

export const chalk = new Proxy(plainChalk, {
  apply(_target, _thisArg, args) {
    return activeChalk()(...args);
  },
  get(_target, prop) {
    return Reflect.get(activeChalk(), prop, activeChalk());
  },
}) as typeof plainChalk;

export function stripAnsi(value: string): string {
  return value.replace(ANSI_PATTERN, '');
}

/**
 * Wrap text in a clickable terminal hyperlink using terminal-link.
 * Falls back to plain text when the terminal doesn't support hyperlinks.
 */
export function makeLink(text: string, url: string): string {
  if (!shouldUseAnsi()) {
    return text;
  }
  return terminalLink(text, url, { fallback: (t) => t });
}

/**
 * Format output based on mode (text or JSON)
 */
export function formatOutput(data: any, json?: boolean): string {
  if (json) {
    return JSON.stringify(data, null, 2);
  }
  return data;
}

/**
 * Create a formatted table
 */
export function createTable(headers: string[], colWidths?: (number | undefined)[]): CliTable {
  const rows: TableRow[] = [];

  return {
    push(row: TableRow): void {
      rows.push(row);
    },
    toString(): string {
      return formatTable(headers, rows, colWidths);
    },
  };
}

export function formatTable(
  headers: string[],
  rows: TableRow[],
  colWidths?: (number | undefined)[]
): string {
  if (!isInteractiveOutput()) {
    return [headers, ...rows].map(formatTsvRow).join('\n');
  }

  const options: Record<string, unknown> = {
    head: headers.map(h => chalk.cyan.bold(h)),
    style: { head: [], border: shouldUseAnsi() ? ['grey'] : [], compact: false },
    wordWrap: true,
  };
  // Only set colWidths if provided - cli-table3 throws when colWidths is undefined (HTPR-3030)
  if (colWidths != null && colWidths.length > 0) {
    options.colWidths = colWidths;
  }
  const table = new Table(options);
  rows.forEach((row) => table.push(row.map(formatDisplayCell)));
  return table.toString();
}

function formatDisplayCell(cell: TableCell): string {
  if (cell == null) return '';
  return String(cell);
}

function formatTsvRow(row: TableRow): string {
  return row.map(formatTsvCell).join('\t');
}

function formatTsvCell(cell: TableCell): string {
  return stripAnsi(formatDisplayCell(cell)).replace(/[\t\r\n]+/g, ' ');
}

/**
 * Extract display string for priority (handles both string and object formats)
 */
function formatPriority(priority: unknown): string {
  if (priority == null) return '-';
  if (typeof priority === 'string') return priority;
  if (typeof priority === 'object' && priority !== null && 'Priority_Value' in priority) {
    return String((priority as { Priority_Value?: string }).Priority_Value ?? '-');
  }
  return '-';
}

/**
 * Format a task list as a table
 */
export function formatTaskTable(tasks: any[], appBase?: string): string {
  const termWidth = process.stdout.columns || 120;
  const remaining = Math.max(60, termWidth - 78);
  const titleWidth = Math.max(30, Math.floor(remaining * 0.55));
  const linkWidth = Math.max(30, remaining - titleWidth);
  const table = createTable(
    ['Ticket', 'Title', 'Status', 'Priority', 'Assigned To', 'Due', 'Link'],
    [undefined, titleWidth, undefined, undefined, undefined, undefined, linkWidth]
  );

  (tasks ?? []).forEach((task) => {
    if (task == null) return;
    const ticketText = task.ticketNumber ?? '-';
    const taskUrl = appBase && task.ticketNumber && task.projectId
      ? (buildTaskLinkFromTicketWithBase(appBase, task.ticketNumber, task.projectId) ?? null)
      : null;
    const ticketCell = taskUrl ? makeLink(ticketText, taskUrl) : ticketText;
    table.push([
      ticketCell,
      task.title ?? '-',
      task.section ?? '-',
      formatPriority(task.priority),
      task.assignees?.[0]?.displayName ?? '-',
      task.dueDate ?? '-',
      taskUrl ?? '-',
    ]);
  });

  return table.toString();
}

export function formatSearchTaskTable(tasks: any[], appBase?: string): string {
  const termWidth = process.stdout.columns || 100;
  // Reserve space for Ticket/Status (auto-sized) plus table borders/padding,
  // then split what's left between Title and Link so neither grows unbounded
  // and forces the terminal itself to soft-wrap the row.
  const remaining = Math.max(60, termWidth - 34);
  const titleWidth = Math.max(30, Math.floor(remaining / 2));
  const linkWidth = Math.max(30, remaining - titleWidth);
  const table = createTable(
    ['Ticket', 'Title', 'Status', 'Link'],
    [undefined, titleWidth, undefined, linkWidth]
  );

  (tasks ?? []).forEach((task) => {
    if (task == null) return;
    const ticketText = task.ticketNumber ?? '-';
    const taskUrl = appBase && task.ticketNumber && task.projectId
      ? (buildTaskLinkFromTicketWithBase(appBase, task.ticketNumber, task.projectId) ?? null)
      : null;
    const ticketCell = taskUrl ? makeLink(ticketText, taskUrl) : ticketText;
    table.push([
      ticketCell,
      task.title ?? '-',
      task.section ?? '-',
      taskUrl ?? '-',
    ]);
  });

  return table.toString();
}

/**
 * Format a project list as a table
 */
export function formatProjectTable(projects: any[], appBase?: string): string {
  const table = createTable(['ID', 'Title', 'Sections', 'Tasks', 'Members', 'Link']);

  projects.forEach(p => {
    const projectUrl = appBase ? buildProjectLink(appBase, p.id) : null;
    const titleCell = projectUrl
      ? makeLink(p.title, projectUrl)
      : p.title;
    table.push([
      p.id.toString(),
      titleCell,
      p.sectionCount?.toString() || '0',
      p.taskCount?.toString() || '0',
      p.memberCount?.toString() || '0',
      projectUrl ?? '-',
    ]);
  });

  return table.toString();
}

/**
 * Extract ordered section names from board_columns_view.
 * Handles array or { columns: [...] } shapes; tries title/name/label per item.
 */
export function extractSectionOrder(boardColumnsView: unknown): string {
  if (!boardColumnsView || !Array.isArray(boardColumnsView) || boardColumnsView.length === 0) return '—';

  return (boardColumnsView as any[])
    .filter((col) => !col.deleted)
    .sort((a, b) => (a.ranking ?? '').localeCompare(b.ranking ?? ''))
    .map((col, i) => `${i + 1}. ${col.section_title ?? `#${col.id}`}`)
    .join('  ') || '—';
}

/**
 * Format a view list as a table
 */
export function formatViewTable(views: any[]): string {
  const table = createTable(['ID', 'Title', 'Visibility', 'Created By', 'Project']);

  views.forEach((v) => {
    const id = v.id;
    const title = v.title;
    const visibility = v.visibility === 'Public'
      ? chalk.green('Public')
      : chalk.magenta('Private');
    const owner = v.owner?.displayName || v.owner?.email || '—';
    const project = v.project?.title || '—';

    table.push([id, title, visibility, owner, project]);
  });

  return table.toString();
}

/**
 * Format error message
 */
export function formatError(message: string): string {
  return chalk.red.bold('Error: ') + message;
}

/**
 * Format success message
 */
export function formatSuccess(message: string): string {
  return chalk.green.bold('✓ ') + message;
}

/**
 * Format warning message
 */
export function formatWarning(message: string): string {
  return chalk.yellow.bold('⚠ ') + message;
}

/**
 * Format info message
 */
export function formatInfo(message: string): string {
  return chalk.blue.bold('ℹ ') + message;
}

/**
 * Format pagination info
 */
export function formatPagination(offset: number, limit: number, total: number, hasMore: boolean): string {
  const end = Math.min(offset + limit, total);
  const msg = `Showing ${offset + 1}-${end} of ${total}`;
  if (hasMore) {
    return formatInfo(`${msg} (use --offset ${offset + limit} for more)`);
  }
  return formatInfo(msg);
}
